import { api as generatedApi, LevelOfSkillDto, MatterEntry, SkillDto, UserDto } from './wemble-api.generated';
import { MaybeDrafted } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { clearCredentials, setCredentials } from 'store/reducers/auth';
import axios from 'axios';
import { WritableDraft } from 'immer/dist/internal';

type TagType =
  | 'Company'
  | 'Competence'
  | 'Group'
  | 'LevelOfSkill'
  | 'ProjectStage'
  | 'ProjectCategory'
  | 'ProjectTimeReport'
  | 'ProjectInvoice'
  | 'NotificationToken'
  | 'Office'
  | 'Project'
  | 'Skill'
  | 'Statistic'
  | 'User'
  | 'Forecast'
  | 'WorkloadHistory'
  | 'WorkplaceHistory'
  | 'Matter'
  | 'ExperienceProfile';

function providesList<R extends { _id?: string | null | undefined }[]>(
  resultsWithIds: R | undefined | null,
  tagType: TagType,
  listSuffix?: string,
): { type: TagType; id: string | undefined }[] {
  return resultsWithIds
    ? [
      { type: tagType, id: `LIST${listSuffix ?? ''}` },
      ...resultsWithIds.map(({ _id }) => ({ type: tagType, id: _id ?? undefined })),
    ]
    : [{ type: tagType, id: `LIST${listSuffix ?? ''}` }];
}


// Update a user in all cached userSearch results
// Iterates through the RTK Query cache to find all userSearch entries and patches the user
export const updateUserInSearchCache = (
  dispatch: any,
  getState: () => any,
  updater: (draft: MaybeDrafted<UserDto | null | undefined>) => void,
  userId?: string,
) => {
  try {
    const state = getState();
    const cacheEntries = state.api?.queries;
    if (!cacheEntries) return;

    for (const key of Object.keys(cacheEntries)) {
      if (!key.startsWith('userSearch(')) continue;
      const entry = cacheEntries[key];
      if (!entry?.data) continue;

      // Extract the original args from the cache key
      const args = entry.originalArgs;
      dispatch(
        searchUsersApi.util.updateQueryData('userSearch', args, (draft: any) => {
          if (!draft?.users) return;
          if (userId) {
            const user = draft.users.find((u: any) => u._id === userId);
            updater(user);
          } else {
            // No userId specified — updater handles finding the right user
            draft.users.forEach((u: any) => updater(u));
          }
        }),
      );
    }
  } catch (e) {
    console.error('updateUserInSearchCache error:', e);
  }
};

// Replace a full user object in all cached userSearch results
const replaceUserInSearchCache = (dispatch: any, getState: () => any, data: UserDto) => {
  updateUserInSearchCache(dispatch, getState, (draft) => {
    if (draft && draft._id === data._id) {
      Object.assign(draft, data);
    }
  });
};

// Remove a user from all cached userSearch results
const removeUserFromSearchCache = (dispatch: any, getState: () => any, userId: string) => {
  try {
    const state = getState();
    const cacheEntries = state.api?.queries;
    if (!cacheEntries) return;

    for (const key of Object.keys(cacheEntries)) {
      if (!key.startsWith('userSearch(')) continue;
      const entry = cacheEntries[key];
      if (!entry?.data) continue;

      const args = entry.originalArgs;
      dispatch(
        searchUsersApi.util.updateQueryData('userSearch', args, (draft: any) => {
          if (!draft?.users) return;
          const index = draft.users.findIndex((u: any) => u._id === userId);
          if (index >= 0) {
            draft.users.splice(index, 1);
            draft.total = Math.max(0, draft.total - 1);
          }
        }),
      );
    }
  } catch (e) {
    console.error('removeUserFromSearchCache error:', e);
  }
};

// Helper function to fetch user forecasts and update global cache
export const refreshUserForecastsInGlobalCache = async (userId: string, dispatch: any) => {
  try {
    // Fetch fresh forecasts for this user
    const userForecastsResult = await dispatch(
      generatedApi.endpoints.getForecastsForUser.initiate({ userId }, { forceRefetch: true })
    );

    if (userForecastsResult.data) {
      // Update global cache with fresh user forecasts
      dispatch(
        generatedApi.util.updateQueryData('getForecasts', {}, (draft) => {
          if (!draft) return;

          // Remove old forecasts for this user
          const otherUsersForecasts = draft.filter(f => f.user !== userId);

          // Add the fresh forecasts for this user
          const userForecastsArray = Array.isArray(userForecastsResult.data) ? userForecastsResult.data : [];
          draft.length = 0;
          draft.push(...otherUsersForecasts, ...userForecastsArray);
        })
      );
    }
  } catch (error) {
    console.error('Failed to refresh user forecasts in global cache:', error);
  }
};

export const api = generatedApi.enhanceEndpoints({
  addTagTypes: [
    'Company',
    'Competence',
    'Group',
    'LevelOfSkill',
    'ProjectStage',
    'ProjectCategory',
    'ProjectTimeReport',
    'ProjectInvoice',
    'NotificationToken',
    'Office',
    'Project',
    'Skill',
    'Statistic',
    'User',
    'Forecast',
    'WorkloadHistory',
    'WorkplaceHistory',
    'Matter',
    'ExperienceProfile'
  ],
  endpoints: {
    refreshToken: {
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          if (!localStorage.getItem('token')) return;
          const { data: token } = await queryFulfilled;
          if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            if (
              (window as any).webkit?.messageHandlers?.setAuthorizationToken?.postMessage &&
              (window as any).webkit?.messageHandlers?.setAuthorizationToken?.postMessage instanceof Function
            ) {
              (window as any).webkit?.messageHandlers?.setAuthorizationToken?.postMessage(token);
            }
            localStorage.setItem('token', token);
            dispatch(setCredentials(token));

          }
        }
        catch { }
      }
    },
    getCompany: {
      providesTags: (result) => (result ? [{ type: 'Company', id: result._id ?? undefined }] : []),
    },
    updateCompany: {
      async onQueryStarted({ }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data) return;
          dispatch(
            api.util.updateQueryData('getGroups', undefined, (draft) => {
              draft.customHtml = data.customHtml;
              draft.companyName = data.name;
            }),
          );
          dispatch(
            api.util.updateQueryData('getCompany', undefined, (draft) => {
              Object.assign(draft, data);
            }),
          );
        } catch { }
      },
    },
    getCompetences: {
      providesTags: (result) => providesList(result, 'Competence'),
    },
    createCompetence: {
      invalidatesTags: [{ type: 'Competence', id: 'LIST' }],
    },
    getCompetence: {
      providesTags: (result) => (result ? [{ type: 'Competence', id: result._id ?? undefined }] : []),
    },
    updateCompetence: {
      invalidatesTags: (result) => [{ type: 'Competence', id: result?._id ?? undefined }],
    },
    deleteCompetence: {
      // Deleting a competency also removes it from everyone's profile, so
      // cached people refetch too. Deletes are rare; the broad refresh is fine.
      invalidatesTags: (result, _error, arg) =>
        result ? [{ type: 'Competence', id: arg.id }, 'User' as TagType] : [],
    },
    addCompetenceToUsers: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Competence', id: arg.id }, ...providesList(arg.body.userIds.map(_id => ({ _id })), 'User')],
    },
    addCompetenceToUser: {
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data: success } = await queryFulfilled;
          if (success) {
            dispatch(api.util.invalidateTags([{ type: "User", id: arg.userId }]))
            dispatch(api.util.invalidateTags([{ type: "Competence", id: arg.id }]))
          }

        }
        catch (error) {
          console.log(error)
        }
      },
    },

    getGroups: {
      providesTags: (result) => providesList(result?.groups, 'Group'),
    },
    getGroupsForUser: {
      providesTags: (result) => providesList(result?.groups, 'Group')
    },
    createGroup: {
      invalidatesTags: [{ type: 'Group', id: 'LIST' }],
    },
    updateGroup: {
      invalidatesTags: (result) => [{ type: 'Group', id: result?._id ?? undefined }],
    },
    deleteGroup: {
      invalidatesTags: [{ type: 'Group', id: 'LIST' }],
    },

    getLevelOfSkills: {
      providesTags: (result) => providesList(result, 'LevelOfSkill'),
    },
    createLevelOfSkill: {
      invalidatesTags: [{ type: 'LevelOfSkill', id: 'LIST' }],
    },
    updateLevelOfSkill: {
      invalidatesTags: (result) => [{ type: 'LevelOfSkill', id: result?._id ?? undefined }],
    },
    deleteLevelOfSkill: {
      invalidatesTags: [{ type: 'LevelOfSkill', id: 'LIST' }],
    },
    getProjectStages: {
      providesTags: (result) => providesList(result, 'ProjectStage'),
    },
    createProjectStage: {
      invalidatesTags: [{ type: 'ProjectStage', id: 'LIST' }],
    },
    updateProjectStage: {
      invalidatesTags: (result) => [{ type: 'ProjectStage', id: result?._id ?? undefined }],
    },
    deleteProjectStage: {
      invalidatesTags: [{ type: 'ProjectStage', id: 'LIST' }],
    },
    getProjectCategories: {
      providesTags: (result) => providesList(result, 'ProjectCategory'),
    },
    createProjectCategory: {
      invalidatesTags: [{ type: 'ProjectCategory', id: 'LIST' }],
    },
    updateProjectCategory: {
      invalidatesTags: (result) => [{ type: 'ProjectCategory', id: result?._id ?? undefined }],
    },
    deleteProjectCategory: {
      invalidatesTags: [{ type: 'ProjectCategory', id: 'LIST' }],
    },
    getProjectTimeReports: {
      providesTags: (result) => providesList(result, 'ProjectTimeReport'),
    },
    createProjectTimeReport: {
      invalidatesTags: (result) => result ? [{ type: 'ProjectTimeReport', id: result._id || '' }, { type: 'Project', id: result.project || '' }] : [],
    },
    updateProjectTimeReport: {
      invalidatesTags: (result) => result ? [{ type: 'ProjectTimeReport', id: result._id || ' ' }, { type: 'Project', id: result.project || '' }] : [],
    },
    deleteProjectTimeReport: {
      invalidatesTags: (result, _error, arg) => result ? [{ type: 'ProjectTimeReport', id: arg.id }, { type: 'Project', id: arg.projectId }] : [],
    },
    getProjectInvoices: {
      providesTags: (result) => providesList(result, 'ProjectInvoice'),
    },
    createProjectInvoice: {
      invalidatesTags: [{ type: 'ProjectInvoice', id: 'LIST' }],
    },
    updateProjectInvoice: {
      invalidatesTags: (result) => [{ type: 'ProjectInvoice', id: result?._id ?? undefined }],
    },
    deleteProjectInvoice: {
      invalidatesTags: [{ type: 'ProjectInvoice', id: 'LIST' }],
    },
    getOffices: {
      providesTags: (result) => providesList(result, 'Office'),
    },
    getOfficesForUser: {
      providesTags: (result) => providesList(result, 'Office'),
    },
    getExperienceProfile: {
      providesTags: (_result, _error, arg) => [{ type: 'ExperienceProfile', id: arg.userId }],
    },
    toggleStar: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'ExperienceProfile', id: arg.userId }],
      async onQueryStarted({ userId, body }, { dispatch, queryFulfilled, getState }) {
        // Read current state via RTK Query selector to determine new value
        const cached = api.endpoints.getExperienceProfile.select({ userId })(getState() as any);
        const current = cached?.data?.entries?.find((e: any) => e.dimension === body.dimension && e.key === body.key);
        const newStarred = !current?.starred;

        // Optimistic update: flip star in the experience profile cache
        const profilePatch = dispatch(
          api.util.updateQueryData('getExperienceProfile', { userId }, (draft: any) => {
            const entry = draft?.entries?.find((e: any) => e.dimension === body.dimension && e.key === body.key);
            if (entry) entry.starred = newStarred;
          }),
        );

        // Optimistic update: flip star in any cached search results that have
        // annotations for this user (same pattern as updateUserInSearchCache)
        const searchPatches: any[] = [];
        try {
          const queries = (getState() as any).api?.queries;
          if (queries) {
            for (const key of Object.keys(queries)) {
              if (!key.startsWith('userSearch(')) continue;
              if (!queries[key]?.data?.experienceAnnotations?.[userId]) continue;
              searchPatches.push(dispatch(
                searchUsersApi.util.updateQueryData('userSearch', queries[key].originalArgs, (draft: any) => {
                  const ann = draft?.experienceAnnotations?.[userId]?.find(
                    (a: any) => a.dimension === body.dimension && a.key === body.key,
                  );
                  if (ann) ann.starred = newStarred;
                }),
              ));
            }
          }
        } catch (e) {
          console.error('toggleStar search cache update error:', e);
        }

        try {
          await queryFulfilled;
        } catch {
          profilePatch.undo();
          searchPatches.forEach(p => p.undo());
        }
      },
    },
    getMatterCommentsForUser: {
      providesTags: ['Matter'],
    },
    getMatterCommentsForMe: {
      providesTags: ['Matter'],
    },
    updateMatterComment: {
      invalidatesTags: ['Matter'],
    },

    updateMatterManager: {
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          window.dispatchEvent(new CustomEvent("updateMatterManager", { detail: body }))
          dispatch(api.util.updateQueryData('getMatterCommentsForMe', undefined, (draft) => {
            Object.assign(draft.find(m => m.matterExternalId == body.matterNumber) as MaybeDrafted<MatterEntry>, {
              manager: body.value ? body.userId : undefined
            });
          }))
        } catch {
        }
      }
    },
    createOffice: {
      invalidatesTags: [{ type: 'Office', id: 'LIST' }],
    },
    updateOffice: {
      invalidatesTags: (result) => [{ type: 'Office', id: result?._id ?? undefined }],
    },
    deleteOffice: {
      invalidatesTags: [{ type: 'Office', id: 'LIST' }],
    },

    getProjects: {
      providesTags: (result) => providesList(result, 'Project'),
    },
    getProject: {
      providesTags: (result) => (result ? [{ type: 'Project', id: result._id ?? undefined }] : []),
    },
    createProject: {
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    },
    updateProject: {
      invalidatesTags: (result) => [{ type: 'Project', id: result?._id ?? undefined }],
    },
    deleteProject: {
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    },
    archiveProject: {
      invalidatesTags: (result) => [{ type: 'Project', id: result?._id ?? undefined }],
    },
    unarchiveProject: {
      invalidatesTags: (result) => [{ type: 'Project', id: result?._id ?? undefined }],
    },
    addProjectToUser: {
      invalidatesTags: (result) => [{ type: 'User', id: result?._id ?? undefined }],
    },
    deleteProjectFromUser: {
      invalidatesTags: (result, _error, arg) =>
        result
          ? [
            { type: 'User', id: arg.userId },
            { type: 'Project', id: arg.id },
          ]
          : [],
    },
    addProjectToUsers: {
      invalidatesTags: (result, _error, arg) =>
        result ? [...arg.body.ids.map((id) => ({ type: 'User' as TagType, id })), { type: 'Project', id: arg.id }] : [],
    },
    createSkill: {
      // A skill can now be created straight from the person card's competence
      // grid, so the person and the competence both need refreshing.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'User' as TagType, id: arg.skillCreationParams.userId ?? undefined },
        { type: 'Competence' as TagType, id: arg.skillCreationParams.competenceId ?? undefined },
      ],
    },
    updateSkill: {
      async onQueryStarted(_, { queryFulfilled, dispatch, getState }) {
        try {
          const { data: skill } = await queryFulfilled;
          const competenceId = (typeof skill.competence == "string" ? skill.competence : skill.competence?._id) || ""

          dispatch(api.util.updateQueryData('getCompetence', { id: competenceId }, draft => {
            if (draft && draft.users) {
              const userIndex = draft.users.findIndex((u) => u.skills[0]._id == skill._id)
              draft.users[userIndex].skills[0].level = skill.level as LevelOfSkillDto
            }
          }))

          // Find which user has this skill by searching through all cached search results and user cards
          var userId: string | undefined;
          try {
            const state = getState();
            const cacheEntries = state.api?.queries;
            if (cacheEntries) {
              for (const key of Object.keys(cacheEntries)) {
                if (userId) break;
                if (key.startsWith('userSearch(')) {
                  const users = (cacheEntries[key] as any)?.data?.users;
                  if (!users) continue;
                  const found = users.find((user: any) => user.skills?.find((s: any) => typeof s == 'string' ? (s == skill._id) : (s._id == skill._id)));
                  if (found?._id) userId = found._id;
                } else if (key.startsWith('getUserById(')) {
                  const user = (cacheEntries[key] as any)?.data;
                  if (!user?.skills) continue;
                  const hasSkill = user.skills.find((s: any) => typeof s == 'string' ? (s == skill._id) : (s._id == skill._id));
                  if (hasSkill) userId = user._id;
                }
              }
            }
          } catch { }

          userId && dispatch(api.util.updateQueryData('getUserById', { id: userId }, draft => {

            if (draft && draft.skills) {
              const skillIndex = draft.skills.findIndex(s => (typeof s == 'string' ? s : s._id) == skill._id);
              if (typeof draft.skills[skillIndex] != 'string') {
                (draft.skills[skillIndex] as WritableDraft<SkillDto> | SkillDto).level = skill.level as LevelOfSkillDto
              }

            }
          }))


          dispatch(api.util.invalidateTags([{ type: "Competence", id: competenceId }]))
          setTimeout(() => dispatch(api.util.prefetch('getCompetences', undefined, {})), 450)

        }
        catch (error) {
          console.log(error)
        }
      },
    },

    getCurrentUser: {
      providesTags: [{ type: 'User', id: 'ME' }],
      async onQueryStarted(_, { dispatch, queryFulfilled, getCacheEntry }) {
        try {
          if (!localStorage.getItem('token')) return;
          const firstFetch = !Boolean(getCacheEntry().data);
          const { data } = await queryFulfilled;
          if (firstFetch && data) {
            console.log("User logged in")
          }
        } catch (error) {
          // @ts-ignore
          if (error?.error?.status === 401) {
            delete axios.defaults.headers.common['Authorization'];
            if (
              (window as any).webkit?.messageHandlers?.deleteAuthorizationToken?.postMessage &&
              (window as any).webkit?.messageHandlers?.deleteAuthorizationToken?.postMessage instanceof Function
            ) {
              (window as any).webkit?.messageHandlers?.deleteAuthorizationToken?.postMessage('Token removed');
            }
            localStorage.removeItem('token');
            localStorage.removeItem('OverviewPageSavedFilters')
            localStorage.removeItem('OverviewPageSavedFiltersV2')
            localStorage.removeItem('wemble-search-filter-v1')
            dispatch({ type: 'RESET' });
            dispatch(clearCredentials());
            window.location.replace('/');
          }
        }
      },
    },
    getUserById: {
      providesTags: (result) => [{ type: 'User', id: result?._id ?? undefined }],
    },
    createUser: {
      invalidatesTags: [
        { type: 'User', id: 'LISTtrue' },
        { type: 'User', id: 'LISTfalse' },
        { type: 'User', id: 'SEARCH' },
      ],
    },
    updateUser: {
      async onQueryStarted({ id, userUpdateParams }, { dispatch, queryFulfilled, getState }) {
        const patchResult = dispatch(
          api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            if (draft?._id === id) {
              Object.assign(draft, userUpdateParams);
            }
          }),
        );
        try {
          const { data } = await queryFulfilled;
          if (!data) return;
          replaceUserInSearchCache(dispatch, getState, data);
          dispatch(
            api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
              if (draft?._id === id) {
                Object.assign(draft, data);
              }
            }),
          );
        } catch {
          patchResult.undo();
        }
      },
    },
    updateUserProfilePicture: {
      invalidatesTags: (result) => [
        { type: 'User', id: result?._id ?? undefined },
        { type: 'User', id: 'ME' },
      ],
    },
    createMultipleUsers: {
      invalidatesTags: [
        { type: 'User', id: 'LISTtrue' },
        { type: 'User', id: 'LISTfalse' },
        { type: 'User', id: 'SEARCH' },
      ],
    },
    deleteUser: {
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        removeUserFromSearchCache(dispatch, getState, id);
        try {
          await queryFulfilled;
        } catch { }
      },
      invalidatesTags: [
        { type: 'User', id: 'LISTtrue' },
        { type: 'User', id: 'LISTfalse' },
      ],
    },
    changeUserTeam: {
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          if (!data) return;
          replaceUserInSearchCache(dispatch, getState, data);
          dispatch(
            api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
              if (draft?._id === id) {
                Object.assign(draft, data);
              }
            }),
          );
        } catch { }
      },
    },
    updateUserAdminStatus: {
      invalidatesTags: (result) => [{ type: 'User', id: result?._id ?? undefined }],
    },
    getForecasts: {
      providesTags: (result) => [
        ...providesList(result, 'Forecast'),
      ],
    },
    getForecastsForUser: {
      providesTags: (result, _error, arg) => [
        { type: 'Forecast', id: `user:${arg.userId}` },
        ...(result?.map(forecast => ({ type: 'Forecast' as TagType, id: forecast._id ?? undefined })) || [])
      ],
    },

    createForecast: {
      async onQueryStarted({ userId, forecastUpsertParams }, { dispatch, queryFulfilled }) {
        // Optimistic update: immediately add the forecast to the cache
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const optimisticForecast = {
          _id: tempId,
          _v: 0,
          created: new Date().toISOString(),
          user: userId,
          company: '', // Will be set by server
          externalSource: undefined,
          ...forecastUpsertParams,
          comment: forecastUpsertParams.comment || undefined, // Convert null to undefined
        };

        const patchResult = dispatch(
          api.util.updateQueryData('getForecasts', {}, (draft) => {
            if (!draft) return;
            draft.push(optimisticForecast);
          })
        );

        try {
          const { data: createdForecast } = await queryFulfilled;
          if (createdForecast) {
            // Replace the optimistic forecast with the real one
            dispatch(
              api.util.updateQueryData('getForecasts', {}, (draft) => {
                if (!draft) return;
                const tempIndex = draft.findIndex(f => f._id === tempId);
                if (tempIndex !== -1) {
                  draft[tempIndex] = createdForecast;
                }
              })
            );
          }
          // Delay refresh to avoid glitchy UI behavior
          // This allows the optimistic update to be visible while still handling backend side effects
          setTimeout(() => {
            refreshUserForecastsInGlobalCache(userId, dispatch);
          }, 100);
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
          // Error handled by invalidatesTags fallback
        }
      }
    },
    updateForecast: {
      async onQueryStarted({ userId, forecastId, forecastUpsertParams }, { dispatch, queryFulfilled }) {
        // Optimistic update: immediately update the forecast in the cache
        const patchResult = dispatch(
          api.util.updateQueryData('getForecasts', {}, (draft) => {
            if (!draft) return;

            const forecastIndex = draft.findIndex(f => f._id === forecastId);
            if (forecastIndex !== -1) {
              // Update the forecast with new values
              Object.assign(draft[forecastIndex], {
                ...forecastUpsertParams,
                comment: forecastUpsertParams.comment || undefined, // Convert null to undefined
                user: userId, // Ensure user is set
              });
            }
          })
        );

        try {
          const { data: updatedForecast } = await queryFulfilled;
          if (updatedForecast) {
            // Update the optimistic forecast with the server response
            // This ensures we have the correct server-side data (like updated timestamps)
            dispatch(
              api.util.updateQueryData('getForecasts', {}, (draft) => {
                if (!draft) return;
                const forecastIndex = draft.findIndex(f => f._id === forecastId);
                if (forecastIndex !== -1) {
                  // Replace with server response while preserving the optimistic position
                  draft[forecastIndex] = updatedForecast;
                }
              })
            );
          }
          // Delay refresh to avoid glitchy UI behavior
          // This allows the optimistic update to be visible while still handling backend side effects
          setTimeout(() => {
            refreshUserForecastsInGlobalCache(userId, dispatch);
          }, 100);
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
          // Error handled by invalidatesTags fallback
        }
      }
    },
    deleteForecast: {
      async onQueryStarted({ userId, forecastId }, { dispatch, queryFulfilled }) {
        // Optimistic update: immediately remove the forecast from the cache
        const patchResult = dispatch(
          api.util.updateQueryData('getForecasts', {}, (draft) => {
            if (!draft) return;
            const forecastIndex = draft.findIndex(f => f._id === forecastId);
            if (forecastIndex !== -1) {
              draft.splice(forecastIndex, 1);
            }
          })
        );

        try {
          await queryFulfilled;
          // Delay refresh to avoid glitchy UI behavior
          // This allows the optimistic update to be visible while still handling backend side effects
          setTimeout(() => {
            refreshUserForecastsInGlobalCache(userId, dispatch);
          }, 100);
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
          // Error handled by invalidatesTags fallback
        }
      }
    },
    updateWorkloadMessage: {
      async onQueryStarted({ workloadMessageCreationParams }, { dispatch, queryFulfilled, getState }) {
        let currentUserId: string | null | undefined = null;
        const updateUserDraft = (draft: MaybeDrafted<UserDto | null | undefined>) => {
          if (!draft) return;
          Object.assign(draft, {
            message: {
              message: workloadMessageCreationParams.message,
            },
          });
        };
        const currentUserPatchResult = dispatch(
          api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            currentUserId = draft?._id;
            updateUserDraft(draft);
          }),
        );
        if (currentUserId) {
          updateUserInSearchCache(dispatch, getState, updateUserDraft, currentUserId);
        }
        try {
          const { data } = await queryFulfilled;
          if (!currentUserId) {
            replaceUserInSearchCache(dispatch, getState, data);
            dispatch(
              api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
                Object.assign(draft || {}, data);
              }),
            );
          }
          if (currentUserId) {
            await refreshUserForecastsInGlobalCache(currentUserId, dispatch);
          }
        } catch {
          currentUserPatchResult.undo();
        }
      },
    },
    refreshUser: {
      invalidatesTags: (result) => [
        { type: 'User', id: result?._id ?? undefined },
        { type: 'User', id: 'ME' },
      ],
    },
    updateStatus: {
      async onQueryStarted({ type, statusUpdateParams }, { dispatch, queryFulfilled, getState }) {
        let currentUserId: string | null | undefined = null;
        const updateUserDraft = (draft: MaybeDrafted<UserDto | null | undefined>) => {
          if (!draft) return;
          const workscale = statusUpdateParams.status === 'away' ? undefined : parseInt(statusUpdateParams.status);

          if (type === 'current') {
            if (statusUpdateParams.status === 'away') {
              draft.onLeave = true;
            } else {
              draft.currentWorkload = workscale;
              draft.onLeave = false;
            }
          } else if (type === 'future' && workscale !== undefined) {
            draft.futureWorkload = workscale;
          }
          (window as any)._workloadLastUpdated_ = (new Date()).toISOString()
        };
        const currentUserPatchResult = dispatch(
          api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            currentUserId = draft?._id;
            updateUserDraft(draft);
          }),
        );
        if (currentUserId) {
          updateUserInSearchCache(dispatch, getState, updateUserDraft, currentUserId);
        }
        try {
          const { data } = await queryFulfilled;
          if (type !== 'current' && type !== 'future' && !currentUserId) {
            replaceUserInSearchCache(dispatch, getState, data);
            dispatch(
              api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
                Object.assign(draft || {}, data);
              }),
            );
          }
        } catch {
          currentUserPatchResult.undo();
        }
      },
    },

    createWorkplaceForecast: {
      async onQueryStarted({ workplaceForecastCreationParams, index }, { dispatch, queryFulfilled, getState }) {
        let currentUserId: string | null | undefined = null;
        const updateUserDraft = (draft: MaybeDrafted<UserDto | null | undefined>) => {
          const { option } = workplaceForecastCreationParams;
          if (draft?.workplaceForecast) {
            draft.workplaceForecast[index] = option;
          }
          const indexNbr = parseInt(index, 10);
          if (draft && indexNbr === 0) {
            draft.onLeave = option === 2;
            draft.atHome = option === 1;
          }
        };
        const currentUserPatchResult = dispatch(
          api.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            currentUserId = draft?._id;
            updateUserDraft(draft);
          }),
        );
        if (currentUserId) {
          updateUserInSearchCache(dispatch, getState, updateUserDraft, currentUserId);
        }
        try {
          const { data } = await queryFulfilled;
          if (!currentUserId) {
            replaceUserInSearchCache(dispatch, getState, data);
          }
        } catch {
          currentUserPatchResult.undo();
        }
      },
    },

    /*getFilteredWorkloadHistory: {
      providesTags: (result) => providesList(result, 'WorkloadHistory'),
    },
    getUserWorkloadHistory: {
      providesTags: (result) => providesList(result, 'WorkloadHistory'),
    },
    getWorkplaceForecast,
    getWorkplaceHistory,
    */
  },
});

export const {
  useRefreshTokenQuery,
  useLoginMutation,
  useRegisterUserMutation,
  useChangePasswordMutation,
  useSetPasswordMutation,
  useResetPasswordMutation,
  useResendUserInviteMutation,
  useUpdateAzureTenantMutation,
  useRemoveAzureTenantMutation,
  useSignInAzureUserWithCodeMutation,
  useSignInAzureUserWithTokenMutation,
  useGetAzureUsersQuery,
  useGetCompanyQuery,
  useUpdateCompanyMutation,
  useGetCompetencesQuery,
  useCreateCompetenceMutation,
  useGetCompetenceQuery,
  useUpdateCompetenceMutation,
  useDeleteCompetenceMutation,
  useAddCompetenceToUsersMutation,
  useAddCompetenceToUserMutation,
  useGetGroupsQuery,
  useGetGroupsForUserQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useGetLevelOfSkillsQuery,
  useCreateLevelOfSkillMutation,
  useUpdateLevelOfSkillMutation,
  useDeleteLevelOfSkillMutation,
  useGetProjectStagesQuery,
  useCreateProjectStageMutation,
  useUpdateProjectStageMutation,
  useDeleteProjectStageMutation,
  useGetProjectCategoriesQuery,
  useCreateProjectCategoryMutation,
  useUpdateProjectCategoryMutation,
  useDeleteProjectCategoryMutation,
  useCreateProjectTimeReportMutation,
  useUpdateProjectTimeReportMutation,
  useDeleteProjectTimeReportMutation,
  useCreateProjectInvoiceMutation,
  useUpdateProjectInvoiceMutation,
  useDeleteProjectInvoiceMutation,
  useGetNotificationTokensQuery,
  useCreateNotificationTokenMutation,
  useDeleteNotificationTokenMutation,
  useGetOfficesQuery,
  useGetOfficesForUserQuery,
  useCreateOfficeMutation,
  useUpdateOfficeMutation,
  useDeleteOfficeMutation,
  useGetProjectsQuery,
  useGetFilteredProjectsMutation,
  useCreateProjectMutation,
  useGetProjectQuery,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
  useAddProjectToUserMutation,
  useDeleteProjectFromUserMutation,
  useAddProjectToUsersMutation,
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  useGetStatisticsMutation,
  useGetCurrentUserQuery,
  useGetUsersQuery,
  useCreateUserMutation,
  useGetUserWorkloadHistoryQuery,
  useGetUserByIdQuery,
  usePrefetch,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUncompletedUserByIdQuery,
  useCreateMultipleUsersMutation,
  useUpdateUserProfilePictureMutation,
  useChangeUserTeamMutation,
  useUpdateUserAdminStatusMutation,
  useGetForecastsQuery,
  useGetForecastsForUserQuery,
  useCreateForecastMutation,
  useUpdateForecastMutation,
  useDeleteForecastMutation,
  useGetFilteredWorkloadHistoryQuery,
  useUpdateWorkloadMessageMutation,
  useRefreshUserMutation,
  useUpdateStatusMutation,
  useCreateWorkplaceForecastMutation,
  useGetWorkplaceHistoryQuery,
  useGetMatterCommentsForUserQuery,
  useGetMatterCommentsForMeQuery,
  useUpdateMatterCommentMutation,
  useUpdateMatterManagerMutation,
  useGetDailyStatusHistoryQuery,
  useGetWeeklyHoursQuery,
  useGetRecentMattersQuery,
  useGetMatterTeamQuery,
  useGetBulkWeeklyHoursMutation,
  useGetBulkRecentMattersMutation,
  useGetExperienceProfileQuery,
  useGetExperienceFacetsQuery,
  useSearchClientsQuery,
  useToggleStarMutation,
  useTrackActionMutation,

} = api;

// POST /users/search as a query (not mutation) for caching + updateQueryData support
const searchUsersApi = api.injectEndpoints({
  endpoints: (build) => ({
    userSearch: build.query<import('./wemble-api.generated').UserSearchResult, import('./wemble-api.generated').UserSearchParams>({
      query: (params) => ({
        url: `/users/search`,
        method: 'POST',
        body: params,
      }),
      // Refetched when users are added (createUser, createMultipleUsers); edits and deletes patch the cache instead.
      providesTags: [{ type: 'User', id: 'SEARCH' }],
    }),
  }),
  overrideExisting: true,
});

export const { useUserSearchQuery } = searchUsersApi;

// POST bulk endpoints as queries for caching
const bulkApi = api.injectEndpoints({
  endpoints: (build) => ({
    bulkWeeklyHours: build.query<
      import('./wemble-api.generated').UserUtilizationEntry[],
      { userIds: string[]; weeks?: number }
    >({
      query: (params) => ({
        url: `/user-experience/bulk/weekly-hours`,
        method: 'POST',
        body: { userIds: params.userIds, weeks: params.weeks },
      }),
    }),
    bulkRecentMatters: build.query<
      import('./wemble-api.generated').UserMattersEntry[],
      { userIds: string[]; days?: number }
    >({
      query: (params) => ({
        url: `/user-experience/bulk/recent-matters`,
        method: 'POST',
        body: { userIds: params.userIds, days: params.days },
      }),
    }),
  }),
  overrideExisting: true,
});

export const { useBulkWeeklyHoursQuery, useBulkRecentMattersQuery } = bulkApi;
